import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLingui } from "@lingui/react/macro"
import { createPortal } from "react-dom"
import { router } from "@inertiajs/react"
import type {
  SearchResponse,
  SearchResult,
  SearchTab,
  SuggestionItem,
} from "@/types/v2/search"
import { SEARCH_MIN_QUERY_LENGTH } from "@/types/v2/search"
import { createSearchRunner } from "@/lib/search-fetch"
import { fetchSuggestions } from "@/lib/search-suggestions"
import {
  getRecentItems,
  getRecentSearches,
  pushRecentItem,
  pushRecentSearch,
  type RecentItem,
} from "@/lib/search-recents"
import { onOpenCommandPalette } from "@/lib/command-palette"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { SearchInput } from "./search-input"
import { SearchTabs } from "./search-tabs"
import { ColdStart } from "./cold-start"
import {
  SearchResultsList,
  type SearchSort,
} from "./search-results-list"
import { PaletteFooter } from "./palette-footer"
import { DrawerPreviewPane } from "./drawer-preview-pane"
import { CommandList } from "./command-list"
import { filterCommands, type PaletteCommand } from "@/lib/commands"

// Global command-palette shell (CON-183).
//
// Mounted ONCE in `DefaultLayout` (next to `<Softphone>`) so
// it persists across navigations on every viewport. Opens on
// ⌘/Ctrl-K and on the sidebar "Search" button (via the
// `command-palette` event bus). Portals to `document.body`
// with a dark scrim — never positioned inside the sidebar
// (CON-108: a transformed ancestor would clip a fixed child).
//
// Two layout shapes from one component, chosen by viewport —
// this is *layout shape* only (the data/fetch/keyboard logic
// is shared and lives at this level, never behind the
// viewport branch, per CLAUDE.md "Build for every viewport"):
//   - Desktop / tablet: a centered modal with a two-pane
//     query state (results left, preview pane right).
//   - Mobile: a full-screen sheet, results full-width, each
//     row tap-navigates (no preview pane).

export function CommandPalette() {
  const { t, i18n } = useLingui()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [tab, setTab] = useState<SearchTab>("all")
  const [response, setResponse] = useState<SearchResponse | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [sort, setSort] = useState<SearchSort>("relevance")

  // Recents are read from localStorage when the palette opens
  // (and after an item is opened) — not on every render — so
  // the cold-start list is stable while the palette is up.
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  // Server-seeded recent records for first-run cold start
  // (CON-186) — fetched on open only when there are no
  // localStorage recents.
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([])

  const inputRef = useRef<HTMLInputElement | null>(null)

  const trimmed = query.trim()
  // `>` switches the palette into command mode (CON-184): the
  // text after `>` filters the command registry instead of
  // running a search. Command mode takes precedence over the
  // min-length search threshold.
  const isCommandMode = trimmed.startsWith(">")
  const commandQuery = isCommandMode ? trimmed.slice(1).trim() : ""
  const isQueryState =
    !isCommandMode && trimmed.length >= SEARCH_MIN_QUERY_LENGTH

  // The rows currently shown (only meaningful in query
  // state). Memoized so the keyboard handler + preview pane
  // read a stable array.
  const results = useMemo<SearchResult[]>(
    () => (isQueryState ? (response?.results ?? []) : []),
    [isQueryState, response],
  )

  // The commands shown in command mode, filtered by the text
  // after `>`. Stable so the keyboard handler reads one array.
  const commands = useMemo<PaletteCommand[]>(
    () => (isCommandMode ? filterCommands(commandQuery, i18n) : []),
    // `i18n` is the instance filterCommands reads; `i18n.locale` is
    // the value that actually changes on a locale switch (the `i18n`
    // ref is stable), so it's what forces the re-filter.
    [isCommandMode, commandQuery, i18n, i18n.locale],
  )

  // ---- open / close --------------------------------------

  const closePalette = useCallback(() => {
    setOpen(false)
  }, [])

  const openPalette = useCallback(() => {
    setOpen(true)
  }, [])

  // Reset transient state whenever the palette closes so the
  // next open starts clean (empty query, All tab, no stale
  // results). Recents are refreshed on open below.
  useEffect(() => {
    if (open) return
    setQuery("")
    setTab("all")
    setResponse(null)
    setActiveIndex(0)
    setSort("relevance")
    setSuggestions([])
  }, [open])

  // Load recents + focus the input on open. On a first run with
  // no opened-history, fetch the business's recent records so
  // the cold start shows the user's own data (CON-186).
  useEffect(() => {
    if (!open) return
    const items = getRecentItems()
    setRecentItems(items)
    setRecentSearches(getRecentSearches())

    const controller = new AbortController()
    if (items.length === 0) {
      fetchSuggestions({ signal: controller.signal })
        .then((res) => setSuggestions(res.suggestions))
        .catch(() => {
          // Soft-fail: the cold start falls back to its warm
          // empty state if suggestions can't load.
        })
    }

    // Focus after paint so the portal node exists.
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => {
      cancelAnimationFrame(id)
      controller.abort()
    }
  }, [open])

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Global ⌘/Ctrl-K toggle + listen for the sidebar button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener("keydown", onKey)
    const cleanup = onOpenCommandPalette(openPalette)
    return () => {
      window.removeEventListener("keydown", onKey)
      cleanup()
    }
  }, [openPalette])

  // ---- search runner -------------------------------------

  // One runner for the lifetime of the component; it owns the
  // debounce timer + abort-previous discipline.
  const runnerRef = useRef<ReturnType<typeof createSearchRunner> | null>(null)
  if (runnerRef.current === null) {
    runnerRef.current = createSearchRunner({
      onResult: (result) => {
        setResponse(result)
        setActiveIndex(0)
      },
      onError: () => {
        // Soft-fail: show the empty state rather than an error
        // code (CLAUDE.md error-tone). A transient failure
        // just looks like "no matches yet".
        setResponse(null)
      },
    })
  }

  // Drive the runner from query + tab. Below the min length
  // we cancel any in-flight request and drop back to cold
  // start. Recording a "recent search" happens on explicit
  // submit/navigate, not on every keystroke.
  useEffect(() => {
    const runner = runnerRef.current
    if (!runner) return
    if (!open) {
      runner.cancel()
      return
    }
    if (isQueryState) {
      runner.run(trimmed, tab)
    } else {
      runner.cancel()
      setResponse(null)
    }
  }, [open, trimmed, tab, isQueryState])

  // Tear down the runner on unmount.
  useEffect(() => {
    return () => runnerRef.current?.cancel()
  }, [])

  // Keep the active row at the top of the command list as the
  // `>` filter narrows, so the highlight never lands on a row
  // that's been filtered out.
  useEffect(() => {
    if (isCommandMode) setActiveIndex(0)
  }, [isCommandMode, commandQuery])

  // ---- navigation / activation ---------------------------

  const activeResult: SearchResult | null = useMemo(() => {
    if (!isQueryState) return null
    return results[activeIndex] ?? null
  }, [isQueryState, results, activeIndex])

  // Record the query into recent searches + navigate. Used by
  // Enter and pointer activation.
  const activateResult = useCallback(
    (result: SearchResult) => {
      pushRecentSearch(trimmed)
      pushRecentItem(result)
      closePalette()
    },
    [trimmed, closePalette],
  )

  const openRecentItem = useCallback(
    (item: RecentItem) => {
      pushRecentItem(item)
      closePalette()
    },
    [closePalette],
  )

  // Run a command (from the `>` list or a cold-start chip) and
  // close the palette — `run` only navigates / opens a form.
  const runCommand = useCallback(
    (command: PaletteCommand) => {
      command.run()
      closePalette()
    },
    [closePalette],
  )

  // Modal keyboard handling on a window listener (not the
  // dialog's onKeyDown) so it works regardless of where focus
  // lands — the input is auto-focused on a rAF, so an
  // immediate keypress can still arrive while focus is on
  // <body>. Matches the codebase's portal-popover / overlay
  // pattern. Esc closes; ↑/↓ move the active row; Enter
  // navigates it the Inertia way (rows are <Link>s for the
  // pointer path, but the keyboard path needs an explicit
  // visit). ←/→ are intentionally NOT bound to tab switching —
  // they'd fight the input's caret movement; tab switching is
  // pointer-driven (Paper marks it nice-to-have only).
  useEffect(() => {
    if (!open) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        closePalette()
        return
      }
      // Command mode (`>`): ↑/↓ move the command selection,
      // Enter runs the active command. The preview pane doesn't
      // render here, so there's nothing to guard against.
      if (isCommandMode) {
        if (commands.length === 0) return
        if (event.key === "ArrowDown") {
          event.preventDefault()
          setActiveIndex((prev) => (prev + 1) % commands.length)
        } else if (event.key === "ArrowUp") {
          event.preventDefault()
          setActiveIndex(
            (prev) => (prev - 1 + commands.length) % commands.length,
          )
        } else if (event.key === "Enter") {
          event.preventDefault()
          const command = commands[activeIndex]
          if (command) runCommand(command)
        }
        return
      }
      if (!isQueryState || results.length === 0) return
      // Keystrokes from editable controls inside the preview
      // pane (inline-edit inputs, the timeline search field)
      // belong to those controls — don't hijack Enter/Arrows
      // for result navigation, or saving a field would instead
      // open the active result and navigate away. The palette's
      // own search input is exempt (Enter opens, arrows move the
      // selection).
      if (isPreviewKeyTarget(event.target)) return
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((prev) => (prev + 1) % results.length)
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex(
          (prev) => (prev - 1 + results.length) % results.length,
        )
      } else if (event.key === "Enter") {
        event.preventDefault()
        const target = results[activeIndex]
        if (target) {
          activateResult(target)
          router.visit(target.href)
        }
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [
    open,
    closePalette,
    isCommandMode,
    commands,
    runCommand,
    isQueryState,
    results,
    activeIndex,
    activateResult,
  ])

  // Tab switch resets selection + sort so the new filter
  // starts at the top in relevance order.
  const onSelectTab = useCallback((next: SearchTab) => {
    setTab(next)
    setActiveIndex(0)
    setSort("relevance")
  }, [])

  // Prefill from an example chip / recent search — focus the
  // input so the user can keep typing.
  const onPrefill = useCallback((value: string) => {
    setQuery(value)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  if (!open) return null
  if (typeof document === "undefined") return null

  const counts = isQueryState ? (response?.counts ?? null) : null

  const body = (
    <>
      <SearchInput
        ref={inputRef}
        value={query}
        onChange={setQuery}
        onClose={closePalette}
        variant={isMobile ? "fullscreen" : "modal"}
      />
      <div className="border-t border-[#F0EDE6]" />
      {/* The type-filter tabs belong to search. Mobile command
          mode drops them (Paper 58C); desktop keeps them above
          the command list for layout continuity (Paper 4F5). */}
      {isCommandMode && isMobile ? null : (
        <>
          <div className="px-3 py-2">
            <SearchTabs active={tab} onSelect={onSelectTab} counts={counts} />
          </div>
          <div className="border-t border-[#F0EDE6]" />
        </>
      )}
      {isCommandMode ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2">
          <CommandList
            commands={commands}
            activeIndex={activeIndex}
            onHoverIndex={setActiveIndex}
            onRun={runCommand}
            filter={commandQuery}
          />
        </div>
      ) : isQueryState ? (
        isMobile ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2">
            <SearchResultsList
              results={results}
              query={trimmed}
              sort={sort}
              onSortChange={setSort}
              activeIndex={activeIndex}
              onHoverIndex={setActiveIndex}
              onActivate={activateResult}
              showChevron
              showSort={false}
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <div className="min-w-0 basis-[56%] overflow-y-auto px-1.5 py-2">
              <SearchResultsList
                results={results}
                query={trimmed}
                sort={sort}
                onSortChange={setSort}
                activeIndex={activeIndex}
                onHoverIndex={setActiveIndex}
                onActivate={activateResult}
              />
            </div>
            <div
              data-testid="command-palette-preview"
              className="min-w-0 basis-[44%] overflow-y-auto border-l border-[#F0EDE6] bg-[#FBF8F1]"
            >
              <DrawerPreviewPane target={activeResult?.previewTarget ?? null} />
            </div>
          </div>
        )
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2.5">
          <ColdStart
            recentItems={recentItems}
            suggestions={suggestions}
            recentSearches={recentSearches}
            onPrefill={onPrefill}
            onOpenItem={openRecentItem}
            onRunCommand={runCommand}
            showChevron={isMobile}
          />
        </div>
      )}
      {!isMobile ? <PaletteFooter /> : null}
    </>
  )

  if (isMobile) {
    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t`Search`}
        data-testid="command-palette"
        className="fixed inset-0 z-[70] flex flex-col bg-white"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {body}
      </div>,
      document.body,
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center">
      <button
        type="button"
        aria-label={t`Close search`}
        onClick={closePalette}
        data-testid="command-palette-scrim"
        className="absolute inset-0 bg-[#1E1B17]/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t`Search`}
        data-testid="command-palette"
        className="relative mt-[12vh] flex max-h-[70vh] w-[720px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[#E5DFD3] bg-white shadow-[0_24px_64px_rgba(30,27,23,0.22),0_2px_8px_rgba(30,27,23,0.08)]"
      >
        {body}
      </div>
    </div>,
    document.body,
  )
}

// True when a keydown originates from the preview pane (the
// reused entry body's inline-edit inputs, timeline search, etc.)
// — those controls own Enter/Arrows. The palette's own search
// input is explicitly exempt so it keeps driving result
// navigation. Used to stop the global key handler from
// navigating away while the user is editing inside the preview.
function isPreviewKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.closest('[data-testid="command-palette-input"]')) {
    return false
  }
  return Boolean(
    target.closest('[data-testid="command-palette-preview"]'),
  )
}
